import cloneDeep from "lodash/cloneDeep";
import { DISCOUNT_MODES, DISCOUNT_TYPES } from "../data/constants/shared";
import { sendsRequestId } from "./provider";
import type { ApplyResult, BillDiscount, DiscountOffer, Sale } from "./types";

/**
 * Writing a discount onto a bill, and reading back whether the bill honoured it.
 *
 * 🔴 **`cloneDeep`, NOT `structuredClone`.** Hermes has no `structuredClone`; apps/mobile
 * polyfills it at `index.js:4`, and shared code that depends on a host's bootstrap order breaks
 * silently — worse, node HAS it natively, so every test here would pass while the mobile app
 * crashed. The same substitution is recorded in `catalog/catalogModel.ts`, which is
 * where this rule is written down; `apps/web`'s own discount code uses `structuredClone` and
 * keeps it, because a browser has one.
 *
 * `cloneDeep` and not `helpers/commonHelpers`'s `deepClone` either: that one is
 * `JSON.parse(JSON.stringify(x))`, which DROPS `undefined` values and flattens Dates. A bill
 * discount legitimately carries `undefined` fields (`useLoyalty`, `notes`, `remarks` on an offer
 * that sets none), and losing the key is not the same as holding the value.
 *
 * Pure and CLONE-ONLY: nothing here mutates its argument, and nothing here prices or saves. A
 * caller builds the entry, writes it into a clone, hands the clone to the pricing engine, then
 * asks `readApplyResult` what happened. Keeping those four steps separate is what makes the
 * partner paths (which price a sale the PARTNER returned) and the local paths (which price the
 * store's) one code path.
 */

/* ── the entry ─────────────────────────────────────────────────────────────────────────── */

/** Everything the entry needs that does not come from the offer itself. */
export interface BillDiscountContext {
  sale: Sale;
  /** The active coupon provider's `metaId`, used when the offer names none. */
  activeProviderMetaId?: string;
  /** `discountStore.discountInfo` — the coupon API's last answer. */
  appliedDiscountInfo?: Record<string, any>;
  /** Editing an already-applied discount: it is replaced and marked overridden. */
  isEditMode?: boolean;
  /** `"voucher"` when the entry came from the coupon API rather than the offers list. */
  source?: string;
  /** A partner reference id from this conversation, preferred over the stored one. */
  referenceId?: string;
  /** The stored partner reference id (`LoyaltyStore.externalRefId`). */
  storedReferenceId?: string;
  /** `redemptionInfo.data.blockedUntil` — how long the partner has reserved the coupon. */
  blockedUntil?: string;
  /** The partner's handle for that reservation, from the verify response's top level. */
  blockId?: string;
}

/**
 * The `billDiscountList` entry for this offer.
 *
 * 🔴 **The offer is spread FIRST and the named fields written over it.** That order is web's and
 * it matters: the partner offers API returns fields the pricing engine reads which are not in
 * this list (`itemIds`, `maxDiscountAmount`, `BuyXGetY`…), so an entry built only from the named
 * fields loses them. Mobile's copy did exactly that — it never spread the offer — which is one
 * reason partner offers could not work there.
 *
 * `discountId` is normalised from `mid || discountId`, because the catalog keys offers by `mid`
 * and the bill by `discountId`.
 *
 * ⚠️ `couponProvider` falls back through three sources in a fixed order: the offer's own, then
 * the provider the coupon API attributed the code to, then the active provider. The order is
 * what makes a coupon typed for a partner that is not currently active still reach that partner.
 */
export const buildBillDiscount = (
  offer: DiscountOffer,
  {
    sale,
    activeProviderMetaId,
    appliedDiscountInfo = {},
    isEditMode = false,
    source,
    referenceId,
    storedReferenceId,
    blockedUntil,
    blockId,
  }: BillDiscountContext,
): BillDiscount => {
  const effectiveReferenceId = referenceId || storedReferenceId;
  const entry: Record<string, any> = {
    ...offer,
    dclass: offer.dclass,
    discountCode: offer.discountCode,
    discountId: offer.mid || offer.discountId,
    discountRate: offer.discountRate,
    discountType: offer.discountType,
    categoryIds: offer.categoryIds,
    mode: offer.mode,
    notes: offer.notes,
    remarks: offer.remarks,
    // Membership-based deals price off this.
    useLoyalty: offer.useLoyalty,
    authenticateCustomer: offer.authenticateCustomer,
    membershipName: (sale as any)?.customerInfo?.membershipName || "",
    saleAmount: (sale as any)?.billSubTotal,
    couponProvider:
      offer.couponProvider || appliedDiscountInfo?.sourceInfo?.provider || activeProviderMetaId || "",
    ...(effectiveReferenceId ? { referenceId: effectiveReferenceId } : {}),
    ...(blockedUntil ? { blockedUntil } : {}),
    ...(blockId ? { blockId } : {}),
  };

  if (isEditMode) {
    entry.notes = offer.notes;
    entry.isOverridden = true;
  }

  // A code typed into the coupon field: the API's answer names the code and its provider, and
  // they override whatever the expanded discount record carried.
  if (source === "voucher") {
    entry.couponCode = appliedDiscountInfo?.couponCode;
    entry.couponProvider = appliedDiscountInfo?.sourceInfo?.provider;
  }

  // Partner correlation id. Asked of the PROFILE — this was
  // `if (isExternalProviderC1C2C3(postData.couponProvider))`, a predicate covering three
  // partners whose only shared trait is needing this id.
  if (sendsRequestId(entry.couponProvider)) {
    if (offer.mode === DISCOUNT_MODES.coupon) {
      entry.couponCode = appliedDiscountInfo?.couponCode;
    }
    entry.requestId = appliedDiscountInfo?.requestId || entry.referenceId;
  }

  return entry;
};

/* ── writing it in ─────────────────────────────────────────────────────────────────────── */

/**
 * A CLONE of `sale` with this entry written into `billDiscountList`.
 *
 * Three shapes, and the third is the one that surprises people:
 *
 * | the entry's mode | what happens |
 * |---|---|
 * | manual / coupon | pushed onto the list |
 * | auto, already on the bill | its `priority` is DELETED, un-suppressing it |
 * | auto, not on the bill | **nothing** — the entry is dropped |
 *
 * 🔴 The third is web's behaviour verbatim, and it is correct rather than a bug: the pricing
 * engine decides which auto discounts a bill qualifies for, so an auto discount the engine has
 * not placed cannot be added by hand. `suppressAutoDiscounts` is how one gets removed, and this
 * is how it comes back. Pushing it would write an entry the engine ignores and the offers row
 * would then read "Applied" forever.
 */
export const writeBillDiscount = (
  sale: Sale,
  entry: BillDiscount,
  /**
   * Replacing an already-applied discount: its previous entry is dropped first.
   *
   * ⚠️ Passed explicitly rather than inferred from the entry's `isOverridden` flag. The two
   * always agree today (`buildBillDiscount` sets one from the other), but an inferred filter
   * would fail silently the moment anything else sets that flag — and a failed filter leaves
   * the OLD rate on the bill beside the new one.
   */
  { isEditMode = false }: { isEditMode?: boolean } = {},
): Sale => {
  const next: any = cloneDeep(sale);
  const entryId = (entry as any).discountId;

  if (!next.billDiscountList) {
    next.billDiscountList = [entry];
    return next;
  }

  if (isEditMode) {
    next.billDiscountList = next.billDiscountList.filter(
      (discount: any) => discount?.discountId !== entryId,
    );
  }

  const list: any[] = next.billDiscountList;
  if ((entry as any).mode === DISCOUNT_MODES.auto) {
    const index = list.findIndex((discount: any) => discount?.discountId === entryId);
    if (index > -1) delete list[index].priority;
  } else {
    list.push(entry);
  }

  return next;
};

/**
 * A CLONE of `sale` with one discount removed from `billDiscountList`.
 *
 * ⚠️ **An auto discount is not deleted — its `priority` is set to `-1`** so the pricing engine
 * re-evaluates it as unapplied. Deleting it would let the engine put it straight back on the
 * next recalculation, which is why the suppression marker exists at all.
 *
 * This is the LOCAL half of a removal only. A partner coupon additionally needs releasing —
 * `removeDiscount` in `./index` pairs the two.
 */
export const stripBillDiscount = (sale: Sale, discount: BillDiscount): Sale => {
  const next: any = cloneDeep(sale);
  const list: any[] = next.billDiscountList;
  if (!list?.length) return next;

  const index = list.findIndex((entry: any) => entry?.discountId === (discount as any)?.discountId);
  if (index > -1) {
    if (list[index]?.mode === DISCOUNT_MODES.auto) list[index].priority = -1;
    else list.splice(index, 1);
  }
  next.billDiscountList = list;
  return next;
};

/**
 * A CLONE of `sale` with every auto discount suppressed.
 *
 * Used when the terminal is offline and the merchant requires customer authentication for
 * discounts: an auto discount would apply itself with nobody able to verify the customer.
 */
export const suppressAutoDiscounts = (sale: Sale): Sale => {
  const next: any = cloneDeep(sale);
  (next.billDiscountList ?? []).forEach((discount: any) => {
    if (discount?.mode === DISCOUNT_MODES.auto) discount.priority = -1;
  });
  return next;
};

/**
 * A CLONE of `sale` carrying at most ONE discount — the incoming one.
 *
 * 🔴 **For single-slot surfaces (the kiosk), where applying an offer REPLACES whatever was
 * there.** A customer with no cashier to explain a combination has one offer, and the "Remove"
 * affordance is the only way to end up with none.
 *
 * ⚠️ **Rewritten, not carried over.** The kiosk hook's `replaceKioskDiscount` used `.find()` to
 * locate "a discount that is not the incoming one" and removed that ONE — so a sale that had
 * somehow collected two kept one of them, and because the incoming discount is not on the sale
 * yet at call time, `find` matched the first entry unconditionally regardless of what was
 * passed. This filters, which is what "at most one" requires.
 *
 * ⚠️ Suppressed auto discounts (`priority: -1`) are KEPT: they are already contributing nothing,
 * and dropping the marker invites the engine to re-apply them beside the new offer.
 */
export const keepOnlyDiscount = (sale: Sale, incomingDiscountId: string): Sale => {
  const next: any = cloneDeep(sale);
  const isIncomingOrSuppressed = (discount: any) =>
    discount?.priority === -1 ||
    [discount?.discountId, discount?.couponCode, discount?.discountCode].some(
      (candidate) => candidate != null && String(candidate) === String(incomingDiscountId),
    );

  next.billDiscountList = (next.billDiscountList ?? []).filter(isIncomingOrSuppressed);
  next.discountList = (next.discountList ?? []).filter(isIncomingOrSuppressed);
  return next;
};

/* ── reading back ──────────────────────────────────────────────────────────────────────── */

/**
 * Did the priced bill honour this discount?
 *
 * 🔴 **Reads `discountList`, the PRICED list, not `billDiscountList`.** The latter contains
 * whatever was just written, so testing it always passes — which is how a discount priced at
 * zero came to read "Applied". The test is a non-zero `netAmount`.
 *
 * ⚠️ A rate-carrying VOUCHER is the documented exception and is checked against
 * `billDiscountList` instead. A voucher's value is the rate itself, and the engine can honour it
 * without producing a `discountList` row — so the strict test would reject a voucher that
 * applied fine.
 */
export const readApplyResult = (pricedSale: Sale, offer: DiscountOffer): ApplyResult => {
  const id = offer?.mid || offer?.discountId;
  const isRatedVoucher = offer?.discountType === DISCOUNT_TYPES.voucher && !!offer?.discountRate;

  const applied = isRatedVoucher
    ? !!((pricedSale as any)?.billDiscountList ?? []).some(
        (discount: any) => discount?.discountId === id,
      )
    : !!((pricedSale as any)?.discountList ?? []).some(
        (discount: any) =>
          discount?.discountId === id && Math.abs(discount?.netAmount || 0) > 0,
      );

  return { applied, sale: pricedSale };
};

/** The entry the priced bill holds for this offer, or `undefined`. For a roll-back. */
export const findBillDiscount = (
  sale: Sale | null | undefined,
  offer: DiscountOffer,
): BillDiscount | undefined =>
  ((sale as any)?.billDiscountList ?? []).find(
    (discount: any) => discount?.discountId === (offer?.mid || offer?.discountId),
  );
